/**
 * SpriteActivityFeed — Dense real-time activity table for sprite progress events.
 * Shows file operations, quality gate results, and phase transitions.
 *
 * Usage:
 *   <SpriteActivityFeed batchId={batchId} />
 *   <SpriteActivityFeed batchId={batchId} filterId={issueId} filterField="issue_id" compact maxHeight="200px" />
 */

import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useSpriteActivity, type SpriteActivityEvent } from '../../api/telemetry';

/* ── Phase + Event styling ──────────────────────────────────── */

const PHASE_STYLE: Record<string, { dot: string; label: string }> = {
  spawning:     { dot: 'bg-neutral-400',   label: 'text-neutral-500' },
  cloning:      { dot: 'bg-neutral-400',   label: 'text-neutral-500' },
  installing:   { dot: 'bg-secondary-400', label: 'text-secondary-600' },
  coding:       { dot: 'bg-coral-500',     label: 'text-coral-600' },
  executing:    { dot: 'bg-coral-500',     label: 'text-coral-600' },
  quality_gate: { dot: 'bg-blue-500',      label: 'text-blue-700' },
  pushing:      { dot: 'bg-accent-500',    label: 'text-accent-700' },
  retrying:     { dot: 'bg-red-500',       label: 'text-red-600' },
  cleanup:      { dot: 'bg-neutral-300',   label: 'text-neutral-400' },
};

const EVENT_STYLE: Record<string, { pill: string; icon?: string }> = {
  sprite_file_created:      { pill: 'text-blue-600 bg-blue-50' },
  sprite_file_modified:     { pill: 'text-neutral-500 bg-neutral-100' },
  sprite_file_deleted:      { pill: 'text-red-600 bg-red-50' },
  sprite_lint_started:      { pill: 'text-neutral-500 bg-neutral-100' },
  sprite_lint_passed:       { pill: 'text-accent-700 bg-accent-50', icon: '\u2713' },
  sprite_lint_failed:       { pill: 'text-red-600 bg-red-50', icon: '\u2717' },
  sprite_typecheck_started: { pill: 'text-neutral-500 bg-neutral-100' },
  sprite_typecheck_passed:  { pill: 'text-accent-700 bg-accent-50', icon: '\u2713' },
  sprite_typecheck_failed:  { pill: 'text-red-600 bg-red-50', icon: '\u2717' },
  sprite_phase_changed:     { pill: 'text-neutral-500 bg-neutral-100' },
  sprite_phase:             { pill: 'text-neutral-500 bg-neutral-100' },
};

function shortEventType(et: string): string {
  return et.replace(/^sprite_/, '');
}

/* ── Props ──────────────────────────────────────────────────── */

interface SpriteActivityFeedProps {
  batchId: string;
  filterId?: string;
  filterField?: 'issue_id' | 'agent_id';
  maxHeight?: string;
  compact?: boolean;
}

/* ── Component ──────────────────────────────────────────────── */

export function SpriteActivityFeed({
  batchId,
  filterId,
  filterField,
  maxHeight = 'auto',
  compact = false,
}: SpriteActivityFeedProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [phaseFilter, setPhaseFilter] = useState<string>('all');

  const queryOpts = filterField === 'issue_id'
    ? { issueId: filterId, limit: 200 }
    : filterField === 'agent_id'
      ? { agentId: filterId, limit: 200 }
      : { limit: 200 };

  const { data, isLoading } = useSpriteActivity(batchId, queryOpts);

  const events = useMemo(() => {
    if (!data?.events) return [];
    if (phaseFilter === 'all') return data.events;
    return data.events.filter(e => e.phase === phaseFilter || e.event_type === `sprite_${phaseFilter}`);
  }, [data?.events, phaseFilter]);

  // Collect unique phases for the filter
  const phases = useMemo(() => {
    if (!data?.events) return [];
    const set = new Set<string>();
    for (const e of data.events) {
      if (e.phase) set.add(e.phase);
    }
    return Array.from(set).sort();
  }, [data?.events]);

  // Active sprite count
  const activeCount = useMemo(() => {
    if (!data?.events) return 0;
    const activeAgents = new Set<string>();
    for (const e of data.events) {
      if (e.agent_id && e.event_type === 'sprite_phase_changed' && e.phase && e.phase !== 'cleanup') {
        activeAgents.add(e.agent_id);
      }
    }
    return activeAgents.size;
  }, [data?.events]);

  // Auto-scroll logic
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    setAutoScroll(atBottom);
  }, []);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  const resumeAutoScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    setAutoScroll(true);
  };

  /* ── Format helpers ─────────────────────────────────────────── */

  function fmtTime(ts: string): string {
    const d = new Date(ts);
    return `${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  }

  function renderPhase(phase: string | null) {
    if (!phase) return null;
    const style = PHASE_STYLE[phase] || PHASE_STYLE.coding;
    return (
      <span className="inline-flex items-center gap-1">
        <span className={`w-1.5 h-1.5 rounded-full inline-block ${style.dot} animate-pulse`} />
        <span className={`${style.label} font-semibold uppercase tracking-wider`}>{phase.replace(/_/g, ' ')}</span>
      </span>
    );
  }

  function renderEventBadge(et: string) {
    const style = EVENT_STYLE[et] || { pill: 'text-neutral-500 bg-neutral-100' };
    return (
      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${style.pill}`}>
        {style.icon ? `${style.icon} ` : ''}{shortEventType(et)}
      </span>
    );
  }

  function renderDetail(e: SpriteActivityEvent): string {
    if (e.file_path) return e.file_path;
    if (e.message) return e.message;
    if (e.error_count) return `${e.error_count} errors`;
    if (e.test_count) return `${e.test_count} tests`;
    if (e.commit_sha) return e.commit_sha.slice(0, 7);
    if (e.phase && e.event_type.includes('phase')) return e.phase;
    if (e.attempt_number) return `attempt ${e.attempt_number}`;
    return '';
  }

  /* ── Render ─────────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4">
        <div className="text-[10px] text-neutral-400 italic text-center">Loading sprite activity...</div>
      </div>
    );
  }

  if (!data?.events.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4">
        <div className="text-[10px] text-neutral-400 italic text-center py-4">No activity yet</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50/60">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-coral-500 animate-pulse" />
          <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-500">Sprite Activity</span>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={phaseFilter}
            onChange={e => setPhaseFilter(e.target.value)}
            className="text-[10px] bg-white border border-neutral-200 rounded px-1.5 py-0.5 text-neutral-600"
          >
            <option value="all">All Phases</option>
            {phases.map(p => (
              <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
            ))}
          </select>
          {activeCount > 0 && (
            <span className="text-[10px] text-neutral-500">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 inline-block mr-1" />
              {activeCount} active
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="overflow-y-auto kanban-scrollbar-subtle"
        style={{ maxHeight }}
      >
        <table className="w-full text-[10px]">
          <thead className="sticky top-0 z-10">
            <tr className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 bg-neutral-50/60 border-b border-neutral-100">
              <th className="text-left px-3 py-1.5 w-12">Time</th>
              {!compact && <th className="text-left px-2 py-1.5 w-20">Story</th>}
              <th className="text-left px-2 py-1.5 w-24">Phase</th>
              <th className="text-left px-2 py-1.5 w-28">Event</th>
              <th className="text-left px-2 py-1.5">Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, idx) => (
              <tr
                key={e.id || idx}
                className="hover:bg-neutral-50/80 transition-colors duration-150 border-b border-neutral-50"
              >
                <td className="px-3 py-1 font-mono tabular-nums text-neutral-400">{fmtTime(e.timestamp)}</td>
                {!compact && (
                  <td className="px-2 py-1 font-mono font-semibold text-neutral-600">
                    {e.issue_id || e.story_id || '\u2014'}
                  </td>
                )}
                <td className="px-2 py-1">{renderPhase(e.phase)}</td>
                <td className="px-2 py-1">{renderEventBadge(e.event_type)}</td>
                <td className="px-2 py-1 font-mono text-neutral-500 truncate max-w-[220px]" title={renderDetail(e)}>
                  {renderDetail(e)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resume auto-scroll pill */}
      {!autoScroll && (
        <button
          onClick={resumeAutoScroll}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-coral-500 text-white text-[10px] font-semibold px-3 py-1 rounded-full shadow-warm-sm hover:bg-coral-600 transition-colors"
        >
          &#x2B07; New activity
        </button>
      )}
    </div>
  );
}

export default SpriteActivityFeed;
